import { type App } from 'vue';

import { registerSharedPlugins } from '@deepidoo/deesplay/plugins';

import { type CorePluginOptions } from './corePluginOptions';
import { setupAuthorization } from './authorization';
import { setupPinia } from './pinia';
import { setupRouter } from './router';
import { setupTanstack } from './tanstack';

export { type CorePluginOptions, defaultOptions } from './corePluginOptions';

const fetchThemeOverrides = async (hostname: string, file: 'light' | 'dark'): Promise<Record<string, string>> => {
	try {
		const response = await fetch(`/brands/${hostname}/${file}.json`, {
			method: 'GET',
			cache: 'no-cache'
		});
		if (!response.ok) {
			return {};
		}
		const contentType = response.headers.get('content-type') ?? '';
		if (contentType.includes('text/html')) {
			return {};
		}
		const data = await response.json();
		if (!data || typeof data !== 'object' || Array.isArray(data)) {
			return {};
		}
		return data as Record<string, string>;
	}
	catch {
		return {};
	}
};

export async function registerCorePlugins(app: App, options?: Partial<CorePluginOptions>): Promise<void> {
	const hostname = window.location.hostname;
	const [lightOverrides, darkOverrides] = await Promise.all([
		fetchThemeOverrides(hostname, 'light'),
		fetchThemeOverrides(hostname, 'dark')
	]);

	registerSharedPlugins(app, {
		...options,
		themes: {
			LIGHT: {
				...options?.themes?.LIGHT,
				...lightOverrides
			},
			DARK: {
				...options?.themes?.DARK,
				...darkOverrides
			}
		}
	});
	app.use(setupPinia()).use(setupRouter(options)).use(setupTanstack()).use(setupAuthorization());
};
